import { Badge, Button, Col, Divider, Input, InputNumber, Popconfirm, Row, Select, Space, Table, Tabs, message } from 'antd';
import { PageHeader } from "@ant-design/pro-layout";
import { DeleteFilled } from "@ant-design/icons";
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { marketplaces, unitTestType } from 'helpers';
import { addMonthlyLongTermStorageFeeAsin, addUnitTestNote, calculateMonthlyAndLongTermStorageFee, deleteMonthlyLongTermStorageFeeAsin, getMonthlyLongTermStorageFeeAsins, getUniteTestNote, updateMonthlyLongTermStorageFeeAsins, updateUnitTestNote } from 'services/unit-tests';
import { getIdFromObject } from 'utils';
import { Helmet } from 'react-helmet';
const { TabPane } = Tabs;

interface DataType {
  _id: string;
  asin: string | null;
  hazmatType: boolean | null;
  weight: number | null;
  length: number | null;
  width: number | null;
  height: number | null;
  expectedMonthlyStorageJanSep: number | null;
  monthlyStorageJanSepLibrary: number | null;
  expectedMonthlyStorageOctDec: number | null;
  monthlyStorageOctDecLibrary: number | null;
  expectedLTFS: number | null;
  LTFSLibrary: number | null;
  match: boolean;
  marketplace: string;
}

const { Option } = Select;

const initNewAsin = {
  asin: null,
  hazmatType: null,
  weight: null,
  length: null,
  width: null,
  height: null,
  expectedMonthlyStorageJanSep: null,
  monthlyStorageJanSepLibrary: null,
  expectedMonthlyStorageOctDec: null,
  monthlyStorageOctDecLibrary: null,
  expectedLTFS: null,
  LTFSLibrary: null,
  match: false,
  marketplace: "US"
}

const MonthlyLongTermStorageFee = () => {
  const [noteObj, setNoteObj] = useState<any>();
  const [note, setNote] = useState<any>();
  const [activedTab, setActivedTab] = useState<string>("US");
  const [dataTable, setDataTable] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [newAsin, setNewAsin] = useState<Omit<DataType, "_id">>(initNewAsin)

  let { marketplace = "US" } = useParams();
  let navigate = useNavigate();

  useEffect(() => {
    getUniteTestNote(unitTestType.MONTHLYANDLTS, marketplace)
      .then(({ data }: any) => setNoteObj((prev: any) => {
        if (data.data && Array.isArray(data.data) && data.data.length > 0) {
          setNote(data.data[0].note)
          return data.data[0]
        }
        setNote(null)
        return null
      }))
      .catch(() =>
        setNote("Here is a note")
      )

    getMonthlyLongTermStorageFeeAsins(marketplace)
      .then(({ data }) => {
        const asins = (data.data && Array.isArray(data.data))
          ? data.data
          : []
        setDataTable(asins)
      })
      .catch(() =>
        message.error("Cannot get asins, please refresh page again")
      )
  }, [activedTab])

  useEffect(() => {
    setActivedTab(marketplace || "US")
  }, [marketplace]);

  const handleOnChangeTab = (tabKey: string) => {
    navigate(`../${tabKey}`, { replace: true });
  };

  const handleOnSaveNote = () => {
    const query = {
      name: "Fulfillment Fee",
      note: note,
      numOfASINs: 0,
      passed: false
    }
    if (!noteObj) {
      addUnitTestNote(unitTestType.MONTHLYANDLTS, marketplace, query)
        .then(({ data }) => {
          message.success("Added note succesfully")
          if (data.data && Array.isArray(data.data) && data.data.length > 0) {
            setNoteObj(data.data[0])
          }
        })
        .catch(() => message.error("Cannot add note"))
    } else {
      const id = noteObj._id // API now returns _id as string
      updateUnitTestNote(id, query)
        .then(({ data }) => {
          message.success("Updated note succesfully")
          if (data.data && Array.isArray(data.data) && data.data.length > 0) {
            setNoteObj(data.data[0])
          }
        })
        .catch(() => message.error("Cannot update note"))
    }
  }

  const handleOnDelete = (id: string) => {
    return deleteMonthlyLongTermStorageFeeAsin(id)
      .then(() => {
        const newData = dataTable.filter((d: any) => d._id !== id);
        setDataTable(newData);
        message.success("Deleted ASIN successfully")
      })
      .catch(() => message.error("Cannot delete ASIN"))
  }

  const handleOnCaculateAsins = (arrAsins: any, isCreate: boolean = true) => {
    setLoading(true)
    const query = arrAsins.reduce((arr: any[], current: any) => {
      let params: any = {
        length: current.length,
        width: current.width,
        height: current.height,
        weight: current.weight,
        hazmatType: current.hazmatType,
        expectedMonthlyStorageJanSep: current.expectedMonthlyStorageJanSep,
        expectedMonthlyStorageOctDec: current.expectedMonthlyStorageOctDec,
        expectedLTFS: current.expectedLTFS,
        marketplace,
        _id: current._id // API now returns _id as string
      }

      arr.push(params)
      return arr
    }, [])

    return calculateMonthlyAndLongTermStorageFee(query)
      .then(({ data }: any) => {
        const result = data.reduce((arr: [any], current: any, currentIndex: number) => {
          const feeJanToSep = Math.round(current.monthlyStorageFees.janToSep * 100) / 100
          const feeOctToDec = Math.round(current.monthlyStorageFees.octToDec * 100) / 100
          const feeStorage = Math.round(current.longTermStorageFees['366'] * 100) / 100
          let newAsinData = {
            ...query[currentIndex],
            asin: arrAsins[currentIndex].asin,
            monthlyStorageJanSepLibrary: feeJanToSep,
            monthlyStorageOctDecLibrary: feeOctToDec,
            LTFSLibrary: feeStorage,
            match:
              feeJanToSep == query[currentIndex].expectedMonthlyStorageJanSep &&
              feeOctToDec == query[currentIndex].expectedMonthlyStorageOctDec &&
              feeStorage == query[currentIndex].expectedLTFS
          }

          arr.push(newAsinData)
          return arr
        }, [])

        if (isCreate) {
          return addMonthlyLongTermStorageFeeAsin(marketplace, result[0])
            .then(({ data }) => {
              message.success(data.message)
              // API now returns _id as string
              const newAsin = data.data

              setDataTable((prev: any) => {
                setNewAsin(initNewAsin)
                return [newAsin, ...prev]
              });
            })
            .catch(() => message.error('Cannot add new ASIN'))
        }

        return updateMonthlyLongTermStorageFeeAsins(result)
          .then(({ data }) => {
            message.success(data.message)

            setDataTable(
              (prev: any) => {
                if (arrAsins.length === 1) {
                  return prev.map((d: any, index: number) => {
                    const id = arrAsins[0]._id // API now returns _id as string
                    const idData = d._id
                    return idData === id ? { ...d, ...result[index] } : d
                  })
                }
                return prev.map((d: any, index: number) => {
                  const id = arrAsins[index]._id // API now returns _id as string
                  const idData = d._id
                  return idData === id ? { ...d, ...result[index] } : d
                })
              }
            );
          })
          .catch(() => message.error('Cannot update ASIN'))
      })
      .catch(error => console.log(error))
      .finally(() => setLoading(false))
  }

  const columns: ColumnsType<DataType> = [
    {
      title: 'ASIN',
      dataIndex: 'asin',
      key: 'asin',
      render: text => <a>{text}</a>,
    },
    {
      title: 'Hazmat type',
      dataIndex: 'hazmatType',
      key: 'hazmatType',
      render: value => <span>{value ? 'Yes' : 'No'}</span>
    },
    {
      title: 'Weight',
      dataIndex: 'weight',
      key: 'weight',
    },
    {
      title: 'Length',
      dataIndex: 'length',
      key: 'length',
    },
    {
      title: 'Width',
      dataIndex: 'width',
      key: 'width',
    },
    {
      title: 'Height',
      dataIndex: 'height',
      key: 'height',
    },
    {
      title: 'Expected M.storage JanSep',
      dataIndex: 'expectedMonthlyStorageJanSep',
      key: 'expectedMonthlyStorageJanSep',
    },
    {
      title: 'M.storage JanSep - Lib',
      dataIndex: 'monthlyStorageJanSepLibrary',
      key: 'monthlyStorageJanSepLibrary',
    },
    {
      title: 'Expected M.storage OctDec',
      dataIndex: 'expectedMonthlyStorageOctDec',
      key: 'expectedMonthlyStorageOctDec',
    },
    {
      title: 'M.storage OctDec - Lib',
      dataIndex: 'monthlyStorageOctDecLibrary',
      key: 'monthlyStorageOctDecLibrary',
    },
    {
      title: 'Expected LTSF',
      dataIndex: 'expectedLTFS',
      key: 'expectedLTFS',
    },
    {
      title: 'LTSF - Lib',
      dataIndex: 'LTFSLibrary',
      key: 'LTFSLibrary',
    },
    {
      title: 'Match',
      dataIndex: 'match',
      key: 'match',
      render: value => (
        <Badge
          text={value ? "Yes" : "No"}
          status={value ? "success" : "error"}
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button type="primary" onClick={() => handleOnCaculateAsins([record], false)}>Library</Button>
          <Popconfirm
            placement="topRight"
            title="Are you sure to delete this ASIN?"
            onConfirm={() => handleOnDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" icon={<DeleteFilled />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Monthly and Long term storage fee | Unit Test</title>
      </Helmet>
      <PageHeader
        className="page-header"
        backIcon={true}
        title="Unit test - Monthly and Long term storage fee"
        footer={
          <Tabs defaultActiveKey={activedTab} type="card" activeKey={activedTab} onChange={handleOnChangeTab}>
            {marketplaces.map(m => (
              <TabPane tab={m.label} key={m.key}>
                <Space direction="vertical" size="large" style={{ width: '100%', marginTop: 30 }}>
                  <Input.Group>
                    <Row gutter={8}>
                      <Col span={18}>
                        <label><strong>Note:</strong></label>
                        <Input.TextArea value={note} onChange={(e) => setNote(e.target.value)} />
                      </Col>
                      <Col span={5} offset={1} style={{ display: 'flex' }}>
                        <Space align="end">
                          <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => handleOnSaveNote()}
                          >
                            Save
                          </Button>
                        </Space>
                      </Col>
                    </Row>
                  </Input.Group>
                  <Divider />
                  <Space>
                    <label><strong>Test ASINs:</strong></label>
                    <Button
                      type="primary"
                      htmlType="submit"
                      onClick={() => handleOnCaculateAsins(dataTable, false)}
                    >
                      Calculate all ASINs using OUR library
                    </Button>
                  </Space>
                  <Divider />
                  <Input.Group>
                    <Row gutter={2}>
                      <Col span={2}>
                        <Input
                          placeholder="Enter new ASIN*"
                          value={newAsin.asin || undefined}
                          onChange={(e: any) => setNewAsin((prev: any) => ({ ...prev, asin: e.target.value }))}
                        />
                      </Col>
                      <Col span={2}>
                        <Select
                          placeholder="Hazmat"
                          value={newAsin.hazmatType}
                          onSelect={(value: boolean) => setNewAsin((prev: any) => ({ ...prev, hazmatType: value }))}
                        >
                          <Option value={false}>No</Option>
                          <Option value={true}>Yes</Option>
                        </Select>
                      </Col>
                      <Col span={2}>
                        <InputNumber
                          controls={false}
                          placeholder="Weight"
                          value={newAsin.weight || undefined}
                          onChange={(value: any) => setNewAsin((prev: any) => ({ ...prev, weight: value }))}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={2}>
                        <InputNumber
                          controls={false}
                          placeholder="Length"
                          value={newAsin.length || undefined}
                          onChange={(value: any) => setNewAsin((prev: any) => ({ ...prev, length: value }))}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={2}>
                        <InputNumber
                          controls={false}
                          placeholder="Width"
                          value={newAsin.width || undefined}
                          onChange={(value: any) => setNewAsin((prev: any) => ({ ...prev, width: value }))}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={2}>
                        <InputNumber
                          controls={false}
                          placeholder="Height"
                          value={newAsin.height || undefined}
                          onChange={(value: any) => setNewAsin((prev: any) => ({ ...prev, height: value }))}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={4}>
                        <InputNumber
                          controls={false}
                          placeholder="Expected M.Storage JanSep"
                          value={newAsin.expectedMonthlyStorageJanSep || undefined}
                          onChange={(value: any) => setNewAsin((prev: any) => ({ ...prev, expectedMonthlyStorageJanSep: value }))}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={4}>
                        <InputNumber
                          controls={false}
                          placeholder="Expected M.Storage OctDec"
                          value={newAsin.expectedMonthlyStorageOctDec || undefined}
                          onChange={(value: any) => setNewAsin((prev: any) => ({ ...prev, expectedMonthlyStorageOctDec: value }))}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={2}>
                        <InputNumber
                          controls={false}
                          placeholder="Expected LTSF"
                          value={newAsin.expectedLTFS || undefined}
                          onChange={(value: any) => setNewAsin((prev: any) => ({ ...prev, expectedLTFS: value }))}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={2}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          onClick={() => handleOnCaculateAsins([newAsin])}
                        >
                          Calculate Ours
                        </Button>
                      </Col>
                    </Row>
                  </Input.Group>
                  <Table columns={columns} dataSource={dataTable} loading={loading} scroll={{ x: true }} />
                </Space>
              </TabPane>
            ))}
          </Tabs>
        }
      />
    </>
  )
};

export default MonthlyLongTermStorageFee;